import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import BarList from './CareBars';
import {
  Panel,
  PanelEmpty,
  CHART_BASE,
  CHART_GRID,
  CHART_COLORS
} from '../../../../component/common/designSystem';
import { WEEKDAY_COLOR } from './careTheme';

/** A figure that has no meaningful value yet, rather than a misleading zero. */
export const orDash = (value, suffix = '') =>
  value === null || value === undefined ? '-' : `${value}${suffix}`;

/**
 * Load one programme, and decide which of the four states the page is in.
 *
 * THE ORDER OF THESE GATES IS LOAD-BEARING. A failed request is checked before
 * suppression, because rendering the withheld state for a network error would
 * claim a privacy protection that never actually ran — and an admin who reads
 * "withheld to protect participants" has been told something false about their
 * own data.
 *
 * Returns `[stats, node]`. A non-null node is what the page should render
 * instead of itself.
 */
export const useProgramme = (fetcher) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return [null, <Loader key="loader" />];
  if (stats?.error) return [null, <LoadFailedPanel key="failed" />];
  if (stats?.suppressed) {
    return [
      null,
      <SuppressedPanel
        key="withheld"
        minimum={stats.minimum}
        what={stats.label || 'this programme'}
      />
    ];
  }
  return [stats, null];
};

/**
 * Thirty days of check-in activity: how many were logged, and how many distinct
 * people logged them.
 *
 * BOTH LINES MATTER TOGETHER. Check-ins alone can climb because two committed
 * people started logging twice a day, which looks identical to the programme
 * reaching more of the workforce. The people line is what separates them.
 */
export const TrendPanel = ({ trend = [], color = CHART_COLORS.brand, unit = 'check-ins' }) => {
  const hasData = trend.some((day) => (day.checkIns || 0) > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [color, CHART_COLORS.gray],
    stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 4] },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  return (
    <Panel title="Last 30 days" subtitle={`Daily ${unit}, and how many people made them`}>
      {hasData ? (
        <Chart
          options={options}
          series={[
            {
              name: unit.charAt(0).toUpperCase() + unit.slice(1),
              data: trend.map((d) => d.checkIns || 0)
            },
            { name: 'People', data: trend.map((d) => d.users || 0) }
          ]}
          type="line"
          height={280}
        />
      ) : (
        <PanelEmpty>
          Nobody enrolled has checked in during the last 30 days. People are signed up, so the
          programme is reaching them — it is the returning that has not happened.
        </PanelEmpty>
      )}
    </Panel>
  );
};

TrendPanel.propTypes = {
  trend: PropTypes.array,
  color: PropTypes.string,
  unit: PropTypes.string
};

/**
 * Which days people check in on.
 *
 * WEEKDAY ONLY, NEVER HOUR OF DAY. None of these collections stores a local
 * date, so an hour taken off the stored timestamp is UTC and would be wrong for
 * most of the workforce. The weekday survives that error; the hour does not.
 * "Who logs their symptoms at 2am" is also not a question these sections exist
 * to answer.
 */
export const WeekdayPanel = ({ rows = [], unit = 'check-ins' }) => {
  const hasData = rows.some((row) => (row.count || 0) > 0);

  const options = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [WEEKDAY_COLOR],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: rows.map((row) => row.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) => `${val} ${unit} — ${rows[dataPointIndex]?.share}%`
      }
    }
  };

  return (
    <Panel title="By day of the week" subtitle="When checking in actually happens">
      {hasData ? (
        <Chart
          options={options}
          series={[{ name: unit, data: rows.map((row) => row.count || 0) }]}
          type="bar"
          height={260}
        />
      ) : (
        <PanelEmpty>No check-ins in the last 30 days to spread across the week.</PanelEmpty>
      )}
    </Panel>
  );
};

WeekdayPanel.propTypes = {
  rows: PropTypes.array,
  unit: PropTypes.string
};

/** A distribution panel that falls back to a sentence rather than a blank box. */
export const SplitPanel = ({ title, subtitle, rows = [], ramp = [], unit, empty }) => (
  <Panel title={title} subtitle={subtitle}>
    {rows.some((row) => (row.count || 0) > 0) ? (
      <BarList rows={rows} unit={unit} colorFor={(index) => ramp[index] || CHART_COLORS.gray} />
    ) : (
      <PanelEmpty>{empty}</PanelEmpty>
    )}
  </Panel>
);

SplitPanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  rows: PropTypes.array,
  ramp: PropTypes.array,
  unit: PropTypes.string,
  empty: PropTypes.node
};

/**
 * The standing note at the foot of every page in both sections.
 *
 * It is repeated rather than stated once on the overview on purpose: an admin
 * can land on any tab from a link, and the limits of what they are looking at
 * should not depend on which door they came through.
 */
export const CareFootnote = ({ minimum }) => (
  <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
    Take-up and engagement only. Nothing anybody recorded is shown here or held behind this page —
    no symptoms, pain or energy scores, weight, medication, diagnosis or cycle information is read
    at all, and there is no list of who is enrolled. Groups smaller than {minimum} are withheld
    entirely rather than rounded.
  </p>
);

CareFootnote.propTypes = {
  minimum: PropTypes.number
};
